// components/dsa.js
import { useEffect, useMemo, useRef } from "react";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

const DEFAULT_BG = "#0a0a0a";
const X_AXIS_H = 20; // reservierter Platz unten für X-Achse (verhindert "unter 0Hz"-Look)
const MAX_DSA_COLUMNS = 360; // 1 hour = 3600 seconds / 10 seconds per column

function timeToMinutes(t) {
  const [hh, mm] = t.split(":").map((v) => parseInt(v, 10));
  return hh * 60 + mm;
}

function minutesToTimeString(m) {
  const hh = Math.floor(m / 60);
  const mm = m % 60;
  return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
}

function generateTimeLabels(startTime, endTime) {
  const start = timeToMinutes(startTime);
  const end = timeToMinutes(endTime);
  const labels = [];
  for (let m = start; m <= end; m++) labels.push(minutesToTimeString(m));
  return labels;
}

// Generate time labels for 1 hour with 10-second intervals (360 columns)
function generateTimeLabelsForHour(startTime) {
  const start = timeToMinutes(startTime);
  const labels = [];
  // Generate labels every 10 seconds for 1 hour (360 intervals)
  // But only show labels at reasonable intervals (every 10 minutes) for readability
  for (let i = 0; i <= 360; i++) {
    const minutes = start + (i / 6); // i/6 minutes = i*10 seconds
    labels.push(minutesToTimeString(minutes));
  }
  return labels;
}

function clamp(v, lo, hi) {
  return Math.max(lo, Math.min(hi, v));
}

function lerp(a, b, t) {
  return a + (b - a) * t;
}

// Blue -> Cyan -> Green -> Yellow -> Orange -> Red
function colorMap(v) {
  const stops = [
    { p: 0.0, c: [0, 60, 160] },   // blue
    { p: 0.2, c: [0, 200, 255] },  // cyan
    { p: 0.4, c: [0, 255, 120] },  // green
    { p: 0.6, c: [255, 220, 0] },  // yellow
    { p: 0.8, c: [255, 140, 0] },  // orange
    { p: 1.0, c: [255, 0, 0] },    // red
  ];

  const x = clamp(v, 0, 1);
  for (let i = 0; i < stops.length - 1; i++) {
    const a = stops[i];
    const b = stops[i + 1];
    if (x >= a.p && x <= b.p) {
      const t = (x - a.p) / (b.p - a.p || 1);
      return [
        Math.round(lerp(a.c[0], b.c[0], t)),
        Math.round(lerp(a.c[1], b.c[1], t)),
        Math.round(lerp(a.c[2], b.c[2], t)),
      ];
    }
  }
  return stops[stops.length - 1].c;
}

// deterministisch-ish
function pseudoRandom(seed) {
  const x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
}

// Baut eine DSA mit sehr starkem Low-Frequency Power-Gradient (rot unten -> blau oben)
// und glatter Frequenz-Transition (Smoothing entlang f)
function buildDSAMatrix({ timeLabels, filledUntilTime, freqMax = 100 }) {
  const timeBins = timeLabels.length;
  const freqBins = freqMax + 1;

  const startM = timeToMinutes(timeLabels[0]);
  const filledM = timeToMinutes(filledUntilTime);
  const filledBins = clamp(filledM - startM + 1, 0, timeBins);

  const mat = new Float32Array(timeBins * freqBins);
  mat.fill(NaN);

  // Power-Model: starkes 1/f (steiler), minimaler Alpha/Beta Anteil, damit >20 Hz sicher blau bleibt
  for (let ti = 0; ti < filledBins; ti++) {
    // langsame Modulation über Zeit
    const slowMod =
      0.92 +
      0.06 * Math.sin((2 * Math.PI * ti) / 45) +
      0.03 * (pseudoRandom(ti * 0.71) - 0.5);

    // kleiner Alpha-Boost (optional, aber sehr dezent)
    const alphaBoost = 0.9 + 0.2 * Math.sin((2 * Math.PI * ti) / 60 + 0.8);

    for (let f = 0; f <= freqMax; f++) {
      const fhz = f;

      // steileres 1/f -> klarer rot->blau Verlauf
      const aperiodic = 1 / Math.pow(fhz + 1, 1.45);

      // sehr dezente Alpha-Struktur ~10 Hz
      const alpha = Math.exp(-Math.pow((fhz - 10) / 4.0, 2)) * 0.25 * alphaBoost;

      // extrem dezentes Beta um ~22 Hz, damit >20 Hz nicht wieder "warm" wird
      const beta = Math.exp(-Math.pow((fhz - 22) / 10.0, 2)) * 0.03;

      // feines Korn
      const grain = (pseudoRandom(ti * 11.3 + f * 0.97) - 0.5) * 0.035;

      // Rohleistung: Low-Freq dominiert
      const raw = (aperiodic * 2.4 + alpha + beta) * slowMod + grain;

      // Scaling -> 0 Hz saturiert rot, >20 Hz fällt in blau
      const v = clamp(raw * 0.85, 0, 1);

      mat[ti * freqBins + f] = v;
    }
  }

  // Frequenz-Smoothing (macht Übergang rot->blau glatter, weniger "Banding")
  // Nur im befüllten Bereich, unbefüllte Zeiten bleiben NaN.
  for (let ti = 0; ti < filledBins; ti++) {
    const base = ti * freqBins;
    // Kopie der Reihe
    const row = new Float32Array(freqBins);
    for (let f = 0; f < freqBins; f++) row[f] = mat[base + f];

    for (let f = 0; f < freqBins; f++) {
      const f0 = clamp(f - 1, 0, freqBins - 1);
      const f1 = f;
      const f2 = clamp(f + 1, 0, freqBins - 1);

      // Kernel: 0.2 / 0.6 / 0.2
      mat[base + f] = clamp(row[f0] * 0.2 + row[f1] * 0.6 + row[f2] * 0.2, 0, 1);
    }
  }

  return { mat, timeBins, freqBins, filledBins };
}

export default function DSA({
  startTime = "15:25",
  endTime = "17:25",
  filledUntilTime = "16:30",
  freqMax = 100,
  height = 220,
  background = DEFAULT_BG,
  title = "DSA",
  xTickTimes = ["15:25", "17:25"],
  // New props for dynamic data
  spectrogramData = null, // Float32Array or null: [timeBins * freqBins] matrix
  timeBins = null, // Number of time columns (120 for 1 hour)
  dynamicMode = false, // If true, use spectrogramData instead of generating fake data
}) {
  const canvasRef = useRef(null);

  const timeLabels = useMemo(() => {
    if (dynamicMode && startTime) {
      return generateTimeLabelsForHour(startTime);
    }
    return generateTimeLabels(startTime, endTime);
  }, [startTime, endTime, dynamicMode]);

  const dsa = useMemo(() => {
    if (dynamicMode) {
      const freqBins = freqMax + 1;
      if (spectrogramData && timeBins) {
        // Use provided spectrogram data
        // Count filled bins (non-NaN columns)
        let filledBins = 0;
        for (let ti = 0; ti < timeBins; ti++) {
          const base = ti * freqBins;
          let hasData = false;
          for (let f = 0; f < freqBins; f++) {
            if (!Number.isNaN(spectrogramData[base + f])) {
              hasData = true;
              break;
            }
          }
          if (hasData) filledBins = ti + 1;
        }
        return { mat: spectrogramData, timeBins, freqBins, filledBins };
      } else {
        // No data yet - return empty matrix
        const defaultTimeBins = timeBins || MAX_DSA_COLUMNS;
        const emptyMatrix = new Float32Array(defaultTimeBins * freqBins);
        emptyMatrix.fill(NaN);
        return { mat: emptyMatrix, timeBins: defaultTimeBins, freqBins, filledBins: 0 };
      }
    }
    // Fallback to fake data generation
    return buildDSAMatrix({ timeLabels, filledUntilTime, freqMax });
  }, [dynamicMode, spectrogramData, timeBins, timeLabels, filledUntilTime, freqMax]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const { mat, timeBins, freqBins } = dsa;

    // 1px pro Bin
    canvas.width = timeBins;
    canvas.height = freqBins;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const img = ctx.createImageData(timeBins, freqBins);
    const data = img.data;

    const bgRGB = [10, 10, 10];

    // y=0 oben, wir wollen 100Hz oben und 0Hz unten -> flip
    for (let y = 0; y < freqBins; y++) {
      const f = (freqBins - 1) - y; // 100..0
      for (let x = 0; x < timeBins; x++) {
        const v = mat[x * freqBins + f];
        const idx = (y * timeBins + x) * 4;

        let r, g, b;
        if (Number.isNaN(v)) {
          [r, g, b] = bgRGB;
        } else {
          [r, g, b] = colorMap(v);
        }

        data[idx + 0] = r;
        data[idx + 1] = g;
        data[idx + 2] = b;
        data[idx + 3] = 255;
      }
    }

    ctx.putImageData(img, 0, 0);
  }, [dsa]);

  // Tick Positionen + bessere Ausrichtung (kein Abschneiden links/rechts)
  const tickPositions = useMemo(() => {
    if (dynamicMode) {
      // For dynamic mode, generate ticks every 10 minutes (360 columns / 60 = 6 intervals)
      const startM = timeToMinutes(startTime);
      const ticks = [];
      for (let i = 0; i <= 6; i++) {
        const minutes = startM + i * 10; // Every 10 minutes
        const p = i / 6; // 0 to 1
        ticks.push({ t: minutesToTimeString(minutes), p });
      }
      return ticks;
    }
    
    const startM = timeToMinutes(startTime);
    const endM = timeToMinutes(endTime);
    const span = endM - startM;

    return xTickTimes
      .map((t) => {
        const m = timeToMinutes(t);
        const p = span > 0 ? (m - startM) / span : 0;
        return { t, p: clamp(p, 0, 1) };
      })
      .filter((x) => x.p >= 0 && x.p <= 1);
  }, [xTickTimes, startTime, endTime, dynamicMode]);

  const getTickStyle = (p) => {
    // Links: nicht -50%, Rechts: nicht +50% (sonst abgeschnitten)
    if (p <= 0.02) return { left: "0%", transform: "translateX(0%)" };
    if (p >= 0.98) return { left: "100%", transform: "translateX(-100%)" };
    return { left: `${p * 100}%`, transform: "translateX(-50%)" };
  };

  const showTitle = Boolean(title);

  return (
    <MDBox
      sx={{
        backgroundColor: background,
        border: dynamicMode ? "none" : "1px solid rgba(255, 255, 255, 0.2)",
        borderRadius: dynamicMode ? 0 : "4px",
        padding: "1rem",
        height,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {showTitle && (
        <MDTypography
          variant="button"
          color="white"
          sx={{ opacity: 0.9, fontWeight: "bold", mb: 0.5 }}
        >
          {title}
        </MDTypography>
      )}

      <MDBox sx={{ display: "flex", height: showTitle ? "calc(100% - 22px)" : "100%" }}>
        {/* Y-Achse */}
        <MDBox
          sx={{
            width: "44px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            pr: 1,
            pt: 0.5,
            pb: `${X_AXIS_H}px`, // exakt derselbe Platz wie unten für X-Achse
          }}
        >
          {[100, 75, 50, 25, 0].map((v) => (
            <MDTypography
              key={v}
              variant="caption"
              color="white"
              sx={{ opacity: 0.85, lineHeight: 1 }}
            >
              {v}
            </MDTypography>
          ))}
        </MDBox>

        {/* Plot Area */}
        <MDBox sx={{ flex: 1, position: "relative" }}>
          {/* Canvas bekommt unten Platz -> keine Überlappung mit X-Achse */}
          <MDBox
            sx={{
              position: "absolute",
              left: 0,
              right: 0,
              top: 0,
              bottom: `${X_AXIS_H}px`,
              overflow: "hidden",
            }}
          >
            <canvas
              ref={canvasRef}
              style={{
                width: "100%",
                height: "100%",
                imageRendering: "pixelated",
                display: "block",
              }}
            />
          </MDBox>

          {/* Legend - only show when not in dynamic mode (external legend is used instead) */}
          {!dynamicMode && (
            <>
              <MDBox
                sx={{
                  position: "absolute",
                  top: 6,
                  right: 6,
                  width: 10,
                  height: 80,
                  borderRadius: 1,
                  overflow: "hidden",
                  border: "1px solid rgba(255,255,255,0.2)",
                  background:
                    "linear-gradient(to top, rgb(0,60,160), rgb(0,200,255), rgb(0,255,120), rgb(255,220,0), rgb(255,140,0), rgb(255,0,0))",
                  zIndex: 2,
                }}
              />
              <MDTypography
                variant="caption"
                color="white"
                sx={{ position: "absolute", top: 4, right: 22, opacity: 0.8, zIndex: 2 }}
              >
                High
              </MDTypography>
              <MDTypography
                variant="caption"
                color="white"
                sx={{ position: "absolute", top: 74, right: 22, opacity: 0.8, zIndex: 2 }}
              >
                Low
              </MDTypography>
            </>
          )}

          {/* X-Achse (eigener Bereich unten) */}
          <MDBox
            sx={{
              position: "absolute",
              left: 0,
              right: 0,
              bottom: 0,
              height: `${X_AXIS_H}px`,
              zIndex: 2,
            }}
          >
            {tickPositions.map(({ t, p }) => (
              <MDTypography
                key={t}
                variant="caption"
                color="white"
                sx={{
                  position: "absolute",
                  ...getTickStyle(p),
                  opacity: 0.9,
                  bottom: 0,
                  whiteSpace: "nowrap",
                }}
              >
                {t}
              </MDTypography>
            ))}
          </MDBox>
        </MDBox>
      </MDBox>
    </MDBox>
  );
}
